<template>
  <section class="menu">
    <NuxtLink to="/" class="menu-logo">
      <svg viewBox="0 0 492 411" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M178.963 297.292C178.244 298.201 178.508 299.545 179.489 300.161C323.896 390.953 498.51 197.632 361.597 63.8117C360.881 63.1117 360.763 61.9519 361.375 61.1593L405.228 4.4031C408.756 -0.164021 415.498 -0.669581 419.567 3.4217C532.346 116.805 490.759 256.283 442.133 316.191C365.283 410.871 239.54 433.881 125.818 368.823C124.967 368.336 123.876 368.542 123.277 369.318L94.612 406.417C92.7638 408.809 89.9163 410.213 86.8962 410.221L9.26358 410.415C1.93391 410.433 -2.21019 402.001 2.27531 396.196L68.8595 310.019C69.3701 309.358 69.4018 308.44 68.9475 307.739C17.1503 227.822 3.94987 145.547 12.7528 99.9577C34.1918 -11.072 146.865 -9.12846 190.198 22.4487C298.994 101.729 238.981 221.357 178.963 297.292ZM109.705 255.779C151.309 192.795 164.012 159.065 163.606 133.966C163.2 108.866 148.867 92.1825 129.806 90.425C110.746 88.6675 94.2761 109.984 96.2441 141.871C97.9898 170.156 106.747 193.148 108.703 197.981C108.886 198.433 109.078 198.872 109.271 199.314C109.35 199.495 109.429 199.676 109.508 199.859C111.333 204.085 111.786 206.912 109.768 212.454C107.846 216.222 106.655 218.705 105.752 220.59C102.908 226.521 102.904 226.529 91.9021 241.984C88.3511 246.58 84.8078 252.332 87.2925 257.583C88.8916 260.963 91.9994 263.334 95.7425 264.028C101.437 265.085 106.161 260.365 109.705 255.779Z"
          fill="currentColor"
          opacity=".9"
        />
      </svg>
    </NuxtLink>
    <nav class="menu-navigation">
      <UiTooltip v-for="(item, slug) in modules" :key="slug">
        <template #trigger>
          <UiTooltipTrigger as-child>
            <NuxtLink class="menu-navigation__item" :class="{ active: route.fullPath.startsWith(item.prefix) }" :to="item.prefix">
              <Icon :name="item.icon" />
            </NuxtLink>
          </UiTooltipTrigger>
        </template>
        <template #content>
          <UiTooltipContent side="right" align="center">
            {{ item.title }}
          </UiTooltipContent>
        </template>
      </UiTooltip>
    </nav>
    <div class="menu-actions">
      <NuxtLink class="menu-actions__item" to="/">
        <Icon name="tabler:search" />
      </NuxtLink>
      <NuxtLink class="menu-actions__item" to="/">
        <Icon name="tabler:help-circle" />
      </NuxtLink>
      <button class="menu-actions__item">
        <Icon name="tabler:settings" />
      </button>
      <div class="menu-actions__divider" />
      <UiDropdownMenu>
        <UiDropdownMenuTrigger as-child>
          <button class="menu-actions__member">{{ user?.name[0] }}{{ user?.surname[0] }}</button>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent class="w-56" side="top" align="start">
          <UiDropdownMenuLabel class="font-normal">
            <div class="flex flex-col gap-y-1.5">
              <p class="text-sm font-medium leading-none">{{ user?.name }} {{ user?.surname }}</p>
              <p class="text-xs leading-none text-muted-foreground">{{ user?.email }}</p>
            </div>
          </UiDropdownMenuLabel>
          <UiDropdownMenuSeparator />
          <UiDropdownMenuGroup>
            <NuxtLink to="/account"><UiDropdownMenuItem icon="tabler:user-cog" title="Hesabım" /></NuxtLink>
            <UiDropdownMenuItem
              icon="tabler:logout"
              title="Çıkış Yap"
              class="hover:!bg-red-50 hover:!text-red-600 cursor-pointer"
              @click="signOut({ callbackUrl: '/login' })"
            />
          </UiDropdownMenuGroup>
        </UiDropdownMenuContent>
      </UiDropdownMenu>
    </div>
  </section>
</template>

<script setup lang="ts">
  const route = useRoute()

  const { data, signOut } = useAuth()
  const { modules } = useModuleStore()

  const user = ref(data.value)
</script>

<style lang="scss" scoped>
  .menu {
    @apply flex flex-col h-full border-r border-gray-50 overflow-hidden relative z-10 bg-inherit flex-none;
    width: var(--sidebar-menu-width);

    &-logo {
      @apply flex items-center justify-center size-14;

      :deep(svg),
      :deep(img) {
        @apply w-6 text-brand-500;
      }
    }
    &-navigation {
      @apply flex flex-col items-center flex-1 gap-1 h-full overflow-auto;

      &__item {
        @apply flex-none relative text-gray-300 size-[38px] flex items-center justify-center rounded-[8px];
        @apply transition-all duration-200;
        font-size: 20px;

        &::before {
          content: "";
          position: absolute;
          display: none;
          width: 2px;
          height: 18px;
          left: -9px;
          background: theme("colors.brand.DEFAULT");
          border-radius: 0 100px 100px 0;
        }
        &:hover {
          @apply bg-gray-40;
        }

        &.router-link-active,
        &.active {
          @apply bg-gray-50/80 text-gray-500;

          &::before {
            @apply flex;
          }
        }
      }
    }
    &-actions {
      @apply flex items-center justify-center flex-col py-3 gap-1.5;

      &__item {
        @apply text-gray-300;
        width: 32px;
        height: 32px;
        padding: 0;
        background: transparent;
        border: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        transition: all 0.2s;
        border-radius: 8px;
        cursor: pointer;

        &:hover {
          @apply bg-gray-50/80 text-gray-500;
        }
      }
      &__divider {
        @apply h-px w-5 mt-0.5 mb-2.5 bg-gray-40;
      }
      &__member {
        @apply flex items-center justify-center size-9 text-sm rounded-[8px] bg-brand/20 text-brand font-bold tracking-tighter border border-brand/5;
      }
    }
  }
</style>
